import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Message,
  Segment
} from 'semantic-ui-react';
import * as microsoftTeams from '@microsoft/teams-js';
import { CONFIG } from '../../helper/config';
import { activeLoader } from '../../actions/view.actions';
import { getCsv } from '../../actions/card-library.actions';
import LoaderComponent from '../loader/loader';

interface IProps {
  is_license_expire: boolean;
  unregisterd_user_msg: string;
  is_unregisterd_user: boolean;
  getCsv: (info: any, token: any,ms?: any)=> void;
}

interface IState {
  unique_id: string;
  teams_context: any;
  open_modal: boolean;
  port_no: string;
}

/**
 * Handles CSV download logic for both Microsoft Teams and WebApp environments.
 *
 * - Detects whether the app is running in **Teams** or **WebApp** mode.
 * - For **Teams**:
 *   - Initializes Microsoft Teams SDK.
 *   - Retrieves context (user, team, channel).
 *   - Fetches authentication token and triggers `getCsv`.
 * - For **WebApp**:
 *   - Listens for messages from the parent frame.
 *   - Extracts context and token, then triggers `getCsv`.
 *
 * The component also handles rendering different UI states depending on:
 * - Whether the user is unregistered.
 * - Whether the license has expired.
 * - Whether the screen width qualifies as mobile.
 *
 * @example
 * ```tsx
 * <DownloadCsv
 *   is_license_expire={false}
 *   is_unregisterd_user={false}
 *   unregisterd_user_msg=""
 *   getCsv={(info, token) => console.log("Fetch CSV", info, token)}
 * />
 * ```
 */
export class DownloadCsv extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      unique_id: '',
      teams_context: {},
      open_modal: false,
      port_no: '',
    };
  }


  /**
   * componentDidMount()
   * Called when the component has been mounted.
   * Handles the logic for both Teams and WebApp.
   * For Teams:
   * - Initializes the Microsoft Teams app.
   * - Gets the context (channel, team, user, etc.).
   * - Gets the auth token.
   * - Calls the getFilterData and getSelectedCard actions.
   * - Sets the component state.
   * For WebApp:
   * - Listens for the message event.
   * - Parses the query parameters.
   * - Calls the getFilterData and getSelectedCard actions.
   * - Sets the component state.
   */
  componentDidMount() {
    const query_params = window.location.search.split("?")[1];
    let isWebApp = query_params
      ? query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[5]
      : "";
    const port_no: any = query_params
      ? query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[0]
      : "";
    const file_name: any = query_params
      ? query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[1]
      : "";
    if (
      query_params &&
      query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[6] === CONFIG.WEBAPP &&
      isWebApp === "notify_me"
    ) {
      isWebApp = CONFIG.WEBAPP;
    }
    if (isWebApp !== CONFIG.WEBAPP) {
      microsoftTeams.app.initialize();
      microsoftTeams.app.notifySuccess();
      let team_context: any = {};
      microsoftTeams.app.getContext().then((context) => {

        team_context.user_id = context.user?.id;
        team_context.user_name = context.user?.userPrincipalName;
        team_context.tenant_id = context.user?.tenant?.id;
        team_context.file_name = file_name;
        team_context.teams_channel_id = context.channel?.id;
        team_context.channelId = "msteams";

        microsoftTeams.authentication.getAuthToken().then((token: string) => {
            let info = {
              va_id: query_params
                ? query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[2]
                : "",
              platform: "teams",
              port_no: port_no,
              ...team_context,
            };
            if(window.screen.width > CONFIG.PVP.MOBILEWIDTH){
              this.props.getCsv(info, token,microsoftTeams)
            }
          }).catch((error) => {
            console.error("error :: ", error);
          });
        });

    } else {
      let webAppData;
      const handleMessage = (event) => {
        if (event.origin !== event?.data?.parentUrl) return;
        webAppData = { ...event.data };
        const token = webAppData.token;
        const info = {
          va_id: query_params
            ? query_params.split(CONFIG.URLQUERYPARAMSEPERATOR)[2]
            : "",
          port_no: port_no,
          user_id: webAppData.aad_object_id,
          user_name: webAppData.email,
          tenant_id: webAppData.tenantId,
          file_name: file_name,
          channelId: "msteams",
          platform: "webapp",
          webAppUrl: webAppData.parentUrl,
        };
        this.props.getCsv(info, token)
        this.setState({
          port_no: port_no,
          teams_context:info,
        });
      };
      window.addEventListener('message', handleMessage);
    }
  }


  /**
   * render()
   * Handles the rendering of the DownloadCsv component
   * - If the user is unregistered, it renders a message with the unregisterd_user_msg
   * - If the license is expired, it renders a message with the license expiration message
   * - If neither of the above apply, it renders a loader if the screen width is above the mobile width threshold, otherwise it renders a message with the CSV download message
   * @returns {JSX.Element} The rendered component
   */
  render() {
    let {   is_license_expire, is_unregisterd_user, unregisterd_user_msg } = this.props;
    let screen_width=window.screen.width;
    if (is_unregisterd_user) {
      return <Segment dangerouslySetInnerHTML={{ __html: unregisterd_user_msg }}
        placeholder textAlign="center" size="large" />
    }
    else if (is_license_expire) {
      return <Segment placeholder textAlign="center" size="large" content={CONFIG.LICENSE_EXPIRE} />
    } else {
      return (
        screen_width > CONFIG.PVP.MOBILEWIDTH ? 
       <LoaderComponent active_loader={true} loading_time={0}/>
       : <Message floating>{CONFIG.CSVDOWNLOADMESSAGE}</Message>
      );
    }
  }
}

/**
 * Maps state to props for the DownloadCsv component.
 * Retrieves the is_license_expire and active_loader from the view state.
 * @param {Object} state - The state of the application.
 * @returns {Object} A props object containing the is_license_expire and active_loader.
 */
const mapStateToProps = (state: any) => {
  return {
    is_license_expire: state.viewState.is_license_expire,
    active_loader: state.viewState.active_loader,
  };
};

export default connect(mapStateToProps, { getCsv, 
  activeLoader,
})(DownloadCsv);
